import { useHistory } from 'react-router-dom';
import { OfferAuction, OfferAuctionStatus, Vehicle } from '../../../../../types/vehicle';
import { SELLER_VEHICLE_STATUS } from '../../../../../constants';
import { modalsToggle } from '../../../../../actions';
import { useDispatch, useSelector } from 'react-redux';
import { useScreen } from 'usehooks-ts';
import { setFocusField } from '../../../../../services/mySales/salesSlice';

export const useSalesActions = (
  currentVehicle: Vehicle,
  offerAuction: OfferAuction | null | undefined,
  offerAuctionStatus: string
) => {
  const { width } = useScreen() || { width: 0 };
  const history = useHistory();
  const dispatch = useDispatch();

  const highestBid = currentVehicle?.highest_bid;

  const { filters } = useSelector((state: any) => state.mySales);
  const isAutoaxessAuction = filters.auctionType === 'autoaxess';

  const isMobile = width < 567;

  const viewButton = {
    title: 'View',
    color: 'secondary',
    className: 'text-center',
    handleClick: (item: Vehicle) => history.push(`/vehicles/${item.id}/details`),
    fullWidth: true,
    withShadow: true,
    isRounded: true,
    size: isMobile ? 'lg' : 'auto'
  };

  const dropReserveButton = {
    title: isAutoaxessAuction ? 'Drop Reserve' : 'Change Reserve',
    color: 'light',
    conditionalRendering: (item: Vehicle) => {
      if (!isAutoaxessAuction)
        return (
          item.status === SELLER_VEHICLE_STATUS.ACTIVE &&
          ![SELLER_VEHICLE_STATUS.ADMIN_ACTION_REQUIRED, SELLER_VEHICLE_STATUS.TIME_ENDED].includes(offerAuctionStatus)
        );
      return (
        offerAuctionStatus === SELLER_VEHICLE_STATUS.IN_PROGRESS && item.reserve_price_set && !item.reserve_price_met
      );
    },
    handleClick: (item: Vehicle) => {
      const modalName = isAutoaxessAuction ? 'dropReserveModal' : 'vehicleEditModal';
      if (!isAutoaxessAuction) {
        dispatch(setFocusField('reserve_price'));
      }
      dispatch(modalsToggle(`${modalName}-${item.id}`));
    },
    fullWidth: true,
    withShadow: true,
    isRounded: true,
    size: isMobile ? 'lg' : 'auto'
  };

  const buttons = [
    viewButton,
    dropReserveButton,
    {
      title: 'Counter',
      color: 'light',
      conditionalRendering: (item: Vehicle) => {
        return [
          SELLER_VEHICLE_STATUS.NEED_SELLER_REVIEW,
          SELLER_VEHICLE_STATUS.BUYER_COUNTER_SENT,
          SELLER_VEHICLE_STATUS.PUBLIC_COUNTER_SENT_TO_BUYERS,
          SELLER_VEHICLE_STATUS.PENDING_ADMIN_APPROVAL,
          SELLER_VEHICLE_STATUS.SELLER_ACTION_REQUIRED
        ].includes(offerAuctionStatus);
      },
      handleClick: (item: Vehicle) => {
        dispatch(modalsToggle(`sendVehicleOffer-${item.id}`));
      },
      fullWidth: true,
      withShadow: true,
      isRounded: true,
      size: isMobile ? 'lg' : 'auto'
    },
    {
      title: 'Accept',
      color: 'primary',
      conditionalRendering: (item: Vehicle) => {
        if (offerAuctionStatus === SELLER_VEHICLE_STATUS.NEED_SELLER_REVIEW) {
          return highestBid?.amount > 0 && !highestBid?.bidder?.is_bot;
        }

        const highestOffer = offerAuction?.offerBids?.[0];
        if (highestOffer && highestOffer.bidder.is_bot) {
          return false;
        }

        return [
          SELLER_VEHICLE_STATUS.BUYER_COUNTER_SENT,
          SELLER_VEHICLE_STATUS.COUNTER_SENT_TO_BUYERS,
          SELLER_VEHICLE_STATUS.PUBLIC_COUNTER_SENT_TO_BUYERS,
          SELLER_VEHICLE_STATUS.NEED_SELLER_REVIEW,
          SELLER_VEHICLE_STATUS.PENDING_ADMIN_APPROVAL,
          SELLER_VEHICLE_STATUS.SELLER_ACTION_REQUIRED
        ].includes(offerAuctionStatus);
      },

      handleClick: (item: Vehicle) => {
        const buyerOffers = offerAuction?.privateOffers?.filter(offer => offer.sender?.role === 'buyer');
        if (
          (offerAuctionStatus === SELLER_VEHICLE_STATUS.NEED_SELLER_REVIEW || buyerOffers?.length === 0) &&
          offerAuction?.status === OfferAuctionStatus.PRIVATE_BIDS
        ) {
          dispatch(modalsToggle(`acceptCounterModal-${item.id}`));
        } else if (
          [
            SELLER_VEHICLE_STATUS.NEED_SELLER_REVIEW,
            SELLER_VEHICLE_STATUS.PUBLIC_COUNTER_SENT_TO_BUYERS,
            SELLER_VEHICLE_STATUS.PENDING_ADMIN_APPROVAL,
            SELLER_VEHICLE_STATUS.SELLER_ACTION_REQUIRED
          ].includes(offerAuctionStatus)
        ) {
          dispatch(modalsToggle(`acceptTopOffer-${item.id}`));
        } else {
          dispatch(modalsToggle(`acceptPrivateOffer-${item.id}`));
        }
      },
      fullWidth: true,
      withShadow: true,
      isRounded: true,
      size: isMobile ? 'lg' : 'auto'
    }
  ];

  const alternativeAuctionButtons = [
    viewButton,
    {
      title: 'Counter to buyers',
      color: 'white',
      conditionalRendering: (item: Vehicle) => {
        return (
          !item.in_battle &&
          [SELLER_VEHICLE_STATUS.IN_PROGRESS, SELLER_VEHICLE_STATUS.NEED_SELLER_REVIEW].includes(offerAuctionStatus)
        );
      },
      handleClick: (item: Vehicle) => {
        dispatch(setFocusField('alternative_offer'));
        dispatch(modalsToggle(`vehicleEditModal-${item.id}`));
      },
      fullWidth: true,
      withShadow: true,
      isRounded: true,
      size: isMobile ? 'lg' : 'auto'
    },
    dropReserveButton,
    {
      title: 'Accept',
      color: 'primary',
      conditionalRendering: (item: Vehicle) => {
        return (
          !item.in_battle &&
          !!item.top_alternative_bid_offer &&
          [SELLER_VEHICLE_STATUS.IN_PROGRESS, SELLER_VEHICLE_STATUS.NEED_SELLER_REVIEW].includes(offerAuctionStatus)
        );
      },
      handleClick: (item: Vehicle) => {
        dispatch(modalsToggle(`acceptTopAlternativeOffer-${item.id}`));
      },
      fullWidth: true,
      withShadow: true,
      isRounded: true,
      size: isMobile ? 'lg' : 'auto'
    }
  ];

  return isAutoaxessAuction ? buttons : alternativeAuctionButtons;
};
